import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReturnQueueTable, CaptureReturnForm } from './components';
import { ReturnLine } from './components/CaptureReturnForm';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { ReturnRow, CaptureReturnDetails, SkuOption } from './driverReturnsTypes';
import { useSkusStore } from '../../../store/skusStore';
import { createTicket } from '../../../services/ticketsApi';
import styles from './DriverReturns.module.css';

const DriverReturns: React.FC = () => {
  // Returns surface as tickets with category='returns'. The driver captures
  // a draft from the route; the back office (Call Center) processes it.
  const navigate = useNavigate();
  const skus = useSkusStore((s) => s.items);
  const fetchSkus = useSkusStore((s) => s.fetchList);

  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => { fetchSkus(); }, [fetchSkus]);

  const skuOptions = useMemo<SkuOption[]>(
    () => skus.map((s) => ({ id: String(s.id), label: `${s.sku_code} — ${s.product_name}` })),
    [skus]
  );

  const captureReturnDetails: CaptureReturnDetails = {
    returnId: '—',
    orderId: '—',
    customer: '—',
  };

  const returns: ReturnRow[] = [];

  const handleSubmit = async (lines: ReturnLine[]) => {
    setSubmitting(true);
    setMessage(null);
    try {
      const subject = `Return draft — ${lines.length} item${lines.length === 1 ? '' : 's'}`;
      const description = lines
        .map((l) => {
          const sku = skus.find((s) => String(s.id) === l.sku);
          const name = sku ? `${sku.sku_code} (${sku.product_name})` : `SKU ${l.sku}`;
          return `• ${name} — qty ${l.quantity}, ${l.condition}${l.notes ? ` · ${l.notes}` : ''}`;
        })
        .join('\n');
      await createTicket({
        category: 'returns',
        priority: 'medium',
        subject,
        description,
      });
      setMessage('Return submitted to back office.');
      setTimeout(() => navigate('/dashboard'), 1200);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Failed to submit return');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <AsyncBoundary
        loading={false}
        error={null}
        empty={returns.length === 0}
        emptyMessage="No returns in your queue"
      >
        <ReturnQueueTable rows={returns} />
      </AsyncBoundary>
      <CaptureReturnForm
        details={captureReturnDetails}
        skuOptions={skuOptions}
        onSubmit={handleSubmit}
        onCancel={() => navigate('/dashboard')}
        submitting={submitting}
      />
      {message && (
        <p style={{ textAlign: 'center', color: message.startsWith('Failed') ? '#DC2626' : '#059669' }}>
          {message}
        </p>
      )}
    </div>
  );
};

export const DriverReturnsPage: React.FC = () => <DriverReturns />;

export { DriverReturns };
export default DriverReturns;
